import { Controller } from '@hotwired/stimulus'

export default class extends Controller {
  static targets = ['header', 'form', 'progressBar', 'percentCompleteProgress', 'backButton', 'submitButton', 'nextText', 'submitText']

  static values = {
    totalFormGroups: Number,
    formSectionIndex: Number,
    currentStep: { type: Number, default: 1 },
    scroll: { type: Boolean, default: true }
  }

  connect() {
    this.totalSteps = this.totalFormGroupsValue + 1 // Include the submit step
    this.updatePercentComplete()
  }

  calculatePercentComplete() {
    return Math.round((this.currentStepValue / this.totalSteps) * 100)
  }

  updatePercentComplete() {
    const percentComplete = this.calculatePercentComplete()
    if (this.hasProgressBarTarget) { this.progressBarTarget.setAttribute('aria-valuenow', percentComplete) }
    this.percentCompleteProgressTarget.style.width = `${percentComplete}%`
  }

  showStep(stepIndex) {
    const step = this.getStepElement(stepIndex)
    if (step) step.classList.remove('hidden')
    if (stepIndex === this.totalFormGroupsValue) {
      this.submitTextTarget.classList.remove('hidden')
      this.nextTextTarget.classList.add('hidden')
    } else {
      this.nextTextTarget.classList.remove('hidden')
      this.submitTextTarget.classList.add('hidden')
    }
  }

  hideStep(stepIndex) {
    const step = this.getStepElement(stepIndex)
    if (step) step.classList.add('hidden')
  }

  getStepElement(stepIndex) {
    return this.element.querySelector(`[data-paginated-form-form-section-index-value="${stepIndex}"]`)
  }

  // When clicking next or back, we want to move the screen to the section
  // header for that page.
  scrollToElement(element) {
    if (!element) return
    const stickyHeight = this.headerTarget.offsetHeight
    const computedStyles = window.getComputedStyle(this.headerTarget)
    const stickyTopOffset = parseInt(computedStyles.top, 10) || 0 // Dynamically get the `top` value

    // Add a buffer for aesthetics (e.g., 5px)
    const yOffset = -(stickyHeight + stickyTopOffset + 5)
    const yPosition = element.getBoundingClientRect().top + window.scrollY + yOffset

    window.scrollTo({
      top: yPosition,
      behavior: "smooth",
    })
  }

  navigateToStep(targetStep) {
    if (!this.getStepElement(targetStep)) return // Guard against invalid steps

    this.hideStep(this.currentStepValue)
    this.showStep(targetStep)

    this.currentStepValue = targetStep
    this.updatePercentComplete()
    this.toggleBackButton()
    const element = this.getStepElement(targetStep)
    this.scrollToElement(element)
  }

  nextPage(event) {
    event.preventDefault()

    if (this.validateRequiredFields()) {
      if (this.currentStepValue !== this.totalFormGroupsValue) {
        this.navigateToStep(this.currentStepValue + 1)
      } else {
        this.formTarget.requestSubmit()
      }
    }
  }

  previousPage(event) {
    event.preventDefault()
    this.navigateToStep(this.currentStepValue - 1)
  }

  toggleBackButton() {
    this.backButtonTarget.classList.toggle('hidden', this.currentStepValue <= 1)
  }

  setError(field) {
    field.setCustomValidity('This field is required')
    field.reportValidity()
    return false
  }

  clearError(field) {
    field.setCustomValidity('')
  }

  validateAtLeastOneSelectedOption(elements) {
    const anyChecked = elements.some(element => element.checked)

    if (!anyChecked) {
      this.setError(elements[0])
      return false
    } else {
      elements.forEach(element => this.clearError(element))
      return true
    }
  }

  validateSelect(field) {
    if (field.value === '' || field.value === 'Select a State') {
      return this.setError(field)
    } else {
      this.clearError(field)
      return true;
    }
  }

  validateDefault(field) {
    if (field.value.trim() === '') {
      return this.setError(field)
    } else {
      this.clearError(field)
      return true
    }
  }


  validateRequiredFields() {
    this.formTarget.dispatchEvent(new CustomEvent('resetErrors'))
    const currentStep = this.getStepElement(this.currentStepValue)
    const requiredElements = currentStep.querySelectorAll('[aria-required="true"]')
    let isValid = true

    for (let i = 0; i < requiredElements.length; i++) {
      const field = requiredElements[i]

      // if the required field is a hidden conditional, ignore it
      if (!field.checkVisibility()) {
        continue
      }

      switch (field.type) {
        case 'fieldset':
          isValid = this.validateAtLeastOneSelectedOption(Array.from(field.elements))
          break
        case 'checkbox':
          isValid = this.validateAtLeastOneSelectedOption(Array.from(document.querySelectorAll(`input[data-target="${field.dataset.target}"]`)))
          break
        case 'select-one':
          isValid = this.validateSelect(field)
          break
        default:
          isValid = this.validateDefault(field)
      }

      if (!isValid) {
        if (this.scrollValue) this.scrollToElement(field)
        break
      }
    }

    return isValid
  }
}